import { useMutation } from '@tanstack/react-query';
import { Button, Field, Input, RadioGroup, Select, StatusPill, Textarea } from '@bb/ui';
import { Fragment, useState } from 'react';
import { ApiError, type AdminApi, type CanvasDefinition, type CanvasSimulationResult } from '../../../api/client.ts';
import { useTestUsers } from '../../composer/shared/ProfileSelector.tsx';
import { SimulationTimeline } from './SimulationTimeline.tsx';
import { buildSimulation, emptySimForm, EVENT_TYPES, isShownIssue, newEventRow, OUTCOME_TEXT, simulationErrorText, type EventRow, type SimForm, type SyntheticFields } from './simulatorForm.ts';

export interface SimulatorPanelProps {
  api: AdminApi;
  environmentId: string;
  canvasId: string;
  /** The graph on screen and whether it differs from the saved draft (the server simulates the saved one). */
  draft: CanvasDefinition;
  dirty: boolean;
  /** Saves the draft (the editor's «Save draft»). */
  onSave?: (() => Promise<unknown>) | undefined;
  saving: boolean;
  /** The running version, if the Canvas was published. */
  published: CanvasDefinition | null;
  /** The last result's path, to highlight on the graph (`null` clears it). */
  onResult: (result: CanvasSimulationResult | null) => void;
  /** Selects a step on the graph (from a validation issue). */
  onFocusStep: (stepId: string) => void;
  onClose: () => void;
}

const SYNTHETIC_FIELDS: Array<{ key: Exclude<keyof SyntheticFields, 'customAttributes' | 'randomBucket'>; label: string; placeholder?: string }> = [
  { key: 'externalId', label: 'External ID', placeholder: 'sim-user-1' },
  { key: 'firstName', label: 'First name' },
  { key: 'email', label: 'Email' },
  { key: 'language', label: 'Language', placeholder: 'pt-BR' },
  { key: 'country', label: 'Country', placeholder: 'BR' },
  { key: 'timezone', label: 'Time zone', placeholder: 'America/Sao_Paulo' },
];

function EventRowFields({ row, index, issues, onChange, onRemove }: { row: EventRow; index: number; issues: Record<string, string>; onChange: (next: EventRow) => void; onRemove: () => void }) {
  const n = index + 1;
  const issue = (field: string) => issues[`events.${index}.${field}`];
  const set = (patch: Partial<EventRow>) => onChange({ ...row, ...patch });
  return (
    <li className="sim-event" role="group" aria-label={`Event ${n}`}>
      <div className="inline-form">
        <Field label={`Event ${n} type`}>
          <Select options={EVENT_TYPES} value={row.type} onChange={(e) => set({ type: e.target.value as EventRow['type'] })} />
        </Field>
        <Field label={`Event ${n} at`} error={issue('at')}>
          <Input value={row.at} spellCheck={false} placeholder="+3d" onChange={(e) => set({ at: e.target.value })} />
        </Field>
        <Button size="sm" variant="ghost" onClick={onRemove}>
          Remove event {n}
        </Button>
      </div>
      {row.type === 'custom_event' && (
        <div className="inline-form">
          <Field label={`Event ${n} name`} error={issue('name')}>
            <Input value={row.name} spellCheck={false} placeholder="deposit_made" onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label={`Event ${n} properties (JSON)`} error={issue('properties')}>
            <Input value={row.properties} spellCheck={false} placeholder='{"amount": 100}' onChange={(e) => set({ properties: e.target.value })} />
          </Field>
        </div>
      )}
      {row.type === 'attribute_change' && (
        <div className="inline-form">
          <Field label={`Event ${n} attribute`} error={issue('attribute')}>
            <Input value={row.attribute} spellCheck={false} placeholder="$language or platform_language" onChange={(e) => set({ attribute: e.target.value })} />
          </Field>
          <Field label={`Event ${n} new value`} hint="JSON (null, 3, true) or text.">
            <Input value={row.value} spellCheck={false} placeholder="es" onChange={(e) => set({ value: e.target.value })} />
          </Field>
        </div>
      )}
      {row.type === 'session_start' && (
        <Field label={`Event ${n} app instance ID (optional)`} error={issue('appInstanceId')}>
          <Input value={row.appInstanceId} spellCheck={false} onChange={(e) => set({ appInstanceId: e.target.value })} />
        </Field>
      )}
      {row.type === 'subscription_change' && (
        <div className="inline-form">
          <Field label={`Event ${n} subscription group ID`} error={issue('groupId')}>
            <Input value={row.groupId} spellCheck={false} onChange={(e) => set({ groupId: e.target.value })} />
          </Field>
          <Field label={`Event ${n} state`}>
            <Select options={[{ value: 'subscribed', label: 'Subscribed' }, { value: 'unsubscribed', label: 'Unsubscribed' }]} value={row.state} onChange={(e) => set({ state: e.target.value as EventRow['state'] })} />
          </Field>
        </div>
      )}
      {row.type === 'message_interaction' && (
        <div className="inline-form">
          <Field label={`Event ${n} message API ID`} error={issue('messageApiId')}>
            <Input value={row.messageApiId} spellCheck={false} onChange={(e) => set({ messageApiId: e.target.value })} />
          </Field>
          <Field label={`Event ${n} interaction`}>
            <Select options={[{ value: 'click', label: 'Click' }, { value: 'open', label: 'Open' }, { value: 'impression', label: 'Impression' }]} value={row.interaction} onChange={(e) => set({ interaction: e.target.value as EventRow['interaction'] })} />
          </Field>
        </div>
      )}
    </li>
  );
}

/**
 * Test Canvas (BB-122, CAN-011): one user — a Content test member with their real data and devices, or a made-up
 * profile — runs through the saved draft or the running version on a virtual clock, with scripted events at relative
 * times. Nothing is stored and nothing is sent (dry run): every Message step shows the exact payload per channel.
 */
export function SimulatorPanel({ api, environmentId, canvasId, draft, dirty, onSave, saving, published, onResult, onFocusStep, onClose }: SimulatorPanelProps) {
  const [form, setForm] = useState<SimForm>(emptySimForm);
  const [touched, setTouched] = useState(false);
  const set = (patch: Partial<SimForm>) => setForm((was) => ({ ...was, ...patch }));
  const testUsers = useTestUsers(environmentId, form.profileKind === 'test');
  const definition = form.source === 'published' && published ? published : draft;
  const check = buildSimulation(form);
  const issue = (key: string) => (touched ? check.issues[key] : undefined);
  const needsSave = form.source === 'draft' && dirty;
  const run = useMutation({
    mutationFn: (body: NonNullable<typeof check.body>) => api.simulateCanvas(environmentId, canvasId, body),
    onMutate: () => onResult(null),
    onSuccess: onResult,
  });
  const submit = () => {
    setTouched(true);
    if (check.body && !needsSave) run.mutate(check.body);
  };
  const stepName = (id: string | null) => (id ? (definition.steps[id]?.name ?? id) : '—');
  const result = run.data;
  const issues = run.error instanceof ApiError && run.error.code === 'canvas_invalid' ? ((run.error.problem.issues as Array<{ message: string; stepId?: string | null }> | undefined) ?? []) : [];
  const testUsersError = testUsers.error instanceof ApiError && testUsers.error.status === 403 ? 'Listing test users needs the profiles.view permission.' : testUsers.isError ? 'Test users could not be loaded.' : null;
  const outcome = result ? OUTCOME_TEXT[result.outcome.status] : null;

  return (
    <section className="panel sim-panel" aria-label="Test Canvas">
      <header className="sim-panel__head">
        <h3>Test Canvas</h3>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close test
        </Button>
      </header>
      <p className="hint">One user through the Canvas on a virtual clock: 30 days take a second. Nothing is stored and nothing is sent.</p>
      <div className="stack-form">
        <RadioGroup
          label="Version"
          options={[
            { value: 'draft', label: 'Saved draft' },
            { value: 'published', label: published ? 'Running version' : 'Running version (not published yet)', disabled: !published },
          ]}
          value={form.source}
          onValueChange={(v) => set({ source: v as SimForm['source'], variantId: '' })}
        />
        {needsSave && (
          <div className="notice notice--warning" role="note">
            The simulator runs the saved draft: save your changes first.{' '}
            {onSave && (
              <Button size="sm" loading={saving} onClick={() => void onSave().catch(() => undefined)}>
                Save draft
              </Button>
            )}
          </div>
        )}
        <fieldset>
          <legend>User</legend>
          <RadioGroup
            label="Run as"
            options={[
              { value: 'synthetic', label: 'Synthetic user' },
              { value: 'test', label: 'Test user (Content test group)' },
            ]}
            value={form.profileKind}
            onValueChange={(v) => set({ profileKind: v as SimForm['profileKind'] })}
          />
          {form.profileKind === 'test' && (
            <>
              {testUsers.isPending && <p aria-busy="true">Loading test users…</p>}
              {testUsersError && <p role="alert">{testUsersError}</p>}
              {testUsers.data && testUsers.data.length === 0 && <p className="hint">No test users yet: add users to an internal group marked for content tests in Settings → Internal groups.</p>}
              {testUsers.data && testUsers.data.length > 0 && (
                <Field label="Test user" error={issue('profile')} hint="Runs with their real attributes, devices, consent and frequency caps.">
                  <Select placeholder="Select…" options={testUsers.data.map((m) => ({ value: m.profileId, label: `${m.externalId ?? m.emailMasked ?? m.profileId} (${m.group})` }))} value={form.testProfileId} onChange={(e) => set({ testProfileId: e.target.value })} />
                </Field>
              )}
            </>
          )}
          {form.profileKind === 'synthetic' && (
            <>
              <p className="hint">Made up for this run, never stored. It has no devices, so every channel counts as reachable.</p>
              <div className="sim-synthetic">
                {SYNTHETIC_FIELDS.map((f) => (
                  <Field key={f.key} label={f.label}>
                    <Input value={form.synthetic[f.key]} placeholder={f.placeholder} spellCheck={false} onChange={(e) => set({ synthetic: { ...form.synthetic, [f.key]: e.target.value } })} />
                  </Field>
                ))}
                <Field label="Random bucket" hint="0–9999; random when empty." error={issue('synthetic.randomBucket')}>
                  <Input value={form.synthetic.randomBucket} inputMode="numeric" onChange={(e) => set({ synthetic: { ...form.synthetic, randomBucket: e.target.value } })} />
                </Field>
              </div>
              <Field label="Custom attributes (JSON)" error={issue('synthetic.customAttributes')}>
                <Textarea rows={2} spellCheck={false} value={form.synthetic.customAttributes} placeholder='{"platform_language": "pt", "tier": "gold"}' onChange={(e) => set({ synthetic: { ...form.synthetic, customAttributes: e.target.value } })} />
              </Field>
            </>
          )}
        </fieldset>
        <fieldset>
          <legend>Entry</legend>
          <Field label="Variant" hint="Default: the variant the running Canvas would give a test user; random for a synthetic one.">
            <Select options={[{ value: '', label: 'Default' }, ...definition.variants.map((v) => ({ value: v.id, label: `${v.name} (${v.percent}%)` }))]} value={form.variantId} onChange={(e) => set({ variantId: e.target.value })} />
          </Field>
          <Field label="Entry context (JSON, optional)" error={issue('context')}>
            <Textarea rows={2} spellCheck={false} value={form.context} placeholder='{"bonus_id": "b-1"}' onChange={(e) => set({ context: e.target.value })} />
          </Field>
          <div className="inline-form">
            <Field label="Entry event (action-based, optional)">
              <Input value={form.entryEventName} spellCheck={false} placeholder="signal_new" onChange={(e) => set({ entryEventName: e.target.value })} />
            </Field>
            <Field label="Entry event properties (JSON)" error={issue('entryEventProperties')}>
              <Input value={form.entryEventProperties} spellCheck={false} placeholder='{"symbol": "EURUSD"}' onChange={(e) => set({ entryEventProperties: e.target.value })} />
            </Field>
          </div>
        </fieldset>
        <fieldset>
          <legend>Event script</legend>
          <p className="hint">When each event happens: +30s, +5m, +2h, +3d, +1w after the start, or an ISO time. Up to 500 events.</p>
          {form.events.length > 0 && (
            <ol className="plain-list">
              {form.events.map((row, i) => (
                <EventRowFields
                  key={i}
                  row={row}
                  index={i}
                  issues={touched ? check.issues : {}}
                  onChange={(next) => set({ events: form.events.map((r, j) => (j === i ? next : r)) })}
                  onRemove={() => set({ events: form.events.filter((_, j) => j !== i) })}
                />
              ))}
            </ol>
          )}
          {issue('events') && <p className="bb-field__error">{issue('events')}</p>}
          <Button size="sm" disabled={form.events.length >= 500} onClick={() => set({ events: [...form.events, newEventRow()] })}>
            Add event
          </Button>
        </fieldset>
        <Field label="Run until (optional)" hint="How far the clock may go, e.g. 31d (at most 800 days). Default: the longest path plus a day." error={issue('untilDuration')}>
          <Input value={form.untilDuration} spellCheck={false} placeholder="31d" onChange={(e) => set({ untilDuration: e.target.value })} />
        </Field>
        <RadioGroup
          label="Delivery"
          options={[
            { value: 'dry_run', label: 'Dry run: render every message, send nothing' },
            { value: 'test_send', label: 'Test send to the test user’s devices — comes with BB-59', disabled: true },
          ]}
          value="dry_run"
          onValueChange={() => undefined}
        />
        {touched && !check.body && !needsSave && (
          <div role="alert">
            <p>Fix the marked fields first.</p>
            {Object.entries(check.issues)
              .filter(([key]) => !isShownIssue(key))
              .map(([key, message]) => (
                <p key={key} className="bb-field__error">
                  {key}: {message}
                </p>
              ))}
          </div>
        )}
        <div className="toolbar">
          <Button variant="primary" onClick={submit} loading={run.isPending} disabled={needsSave}>
            Run test
          </Button>
        </div>
      </div>

      {run.isError && (
        <div role="alert" className="sim-panel__error">
          <p>{simulationErrorText(run.error)}</p>
          {issues.length > 0 && (
            <ul>
              {issues.map((i, n) => (
                <li key={n}>
                  {i.message}
                  {i.stepId && draft.steps[i.stepId] && (
                    <>
                      {' '}
                      <Button size="sm" variant="ghost" onClick={() => onFocusStep(i.stepId!)}>
                        Show «{draft.steps[i.stepId]!.name}»
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {result && outcome && (
        <section className="sim-panel__result" aria-label="Simulation result">
          {result.masked && (
            <p className="notice notice--warning" role="note">
              Personal attributes of the test user show as ••• in payloads: seeing them needs the pii.view permission.
            </p>
          )}
          <p role="status">
            <StatusPill tone={outcome.tone}>{outcome.label}</StatusPill> {result.outcome.reason ? `${result.outcome.reason} · ` : ''}
            {result.outcome.status === 'waiting' ? `in «${stepName(result.outcome.stepId)}» when the clock stopped` : result.outcome.stepId ? `at «${stepName(result.outcome.stepId)}»` : ''}
          </p>
          <dl className="facts">
            {[
              ['Version', result.definitionSource === 'draft' ? 'Saved draft' : 'Running version'],
              ['User', result.profile.kind === 'test' ? `Test user ${result.profile.profileId}` : 'Synthetic user'],
              ['Variant', definition.variants.find((v) => v.id === result.outcome.variantId)?.name ?? result.outcome.variantId ?? '—'],
              ['Virtual time', `${result.outcome.startedAt} → ${result.outcome.endedAt}`],
              ['Steps run', String(result.outcome.stepExecutions)],
              ['Events', `${result.outcome.eventsProcessed} processed, ${result.outcome.eventsSkipped} after the end`],
              ['Took', `${result.outcome.durationMs} ms`],
            ].map(([label, value]) => (
              <Fragment key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </Fragment>
            ))}
          </dl>
          <p aria-label="Path">Path: {result.outcome.path.map(stepName).join(' → ') || '—'}</p>
          <SimulationTimeline trace={result.trace} stepName={stepName} />
        </section>
      )}
    </section>
  );
}
